import axios from 'axios';
import type { ClientAccountDeletion } from '@/types/client';
import { extractErrorMessage } from '@/utils/toast-utils';

type AccountDeletionSource = {
  status?: string | null;
  accountDeletion?: ClientAccountDeletion | null;
};

export const isPendingDeletionClient = (client: AccountDeletionSource | null | undefined): boolean => {
  if (!client) return false;
  if (client.status === 'pending_deletion') return true;
  return client.accountDeletion?.status === 'pending_deletion';
};

export const getClientAccountDeletion = (
  client: AccountDeletionSource | null | undefined
): ClientAccountDeletion | null => {
  const accountDeletion = client?.accountDeletion;
  if (!accountDeletion || typeof accountDeletion !== 'object') return null;
  if (accountDeletion.status !== 'pending_deletion') return null;
  return accountDeletion;
};

export const getClientRecoverableUntil = (
  client: AccountDeletionSource | null | undefined
): string | null => {
  const accountDeletion = getClientAccountDeletion(client);
  const deadline = accountDeletion?.recoverableUntil || accountDeletion?.purgeAt || null;
  return deadline?.trim() || null;
};

export const formatClientRestoreDeadline = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const getDeleteClientErrorCopy = (error: unknown): { title: string; message: string } => {
  const message = extractErrorMessage(error);

  if (!axios.isAxiosError(error)) {
    return { title: 'Failed to delete client', message };
  }

  const status = error.response?.status;
  const data = error.response?.data;
  const body = data && typeof data === 'object' && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : null;
  const code = typeof body?.code === 'string' ? body.code : '';
  const hasConsentBlock = Boolean(body?.consent) || /consent/i.test(`${code} ${message}`);

  if (status === 404) {
    return {
      title: 'Client not found',
      message: message || 'This client could not be found.',
    };
  }

  if (status === 403) {
    return {
      title: hasConsentBlock ? 'Consent blocked this delete' : 'Cannot delete client',
      message: message || 'You do not have permission to delete this client.',
    };
  }

  if (status === 400 || status === 401) {
    return {
      title: status === 401 ? 'Sign in required' : 'Cannot delete client',
      message: message || (status === 401
        ? 'You need to be signed in to delete a client.'
        : 'This client id is invalid.'),
    };
  }

  return { title: 'Failed to delete client', message };
};
